import { useEffect, useMemo, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Stack } from "@mui/material";
import { useForm } from "react-hook-form";
import { isEqual } from "lodash";

import { WorkOrderState } from "../../../models";
import { workOrderStateSchema as schema } from "../schemas";
import {
  useAddWorkOrderState,
  usePatchWorkOrderState,
} from "../../../hooks/work-order-state";
import { FormContainer } from "../../layout";
import {
  FormSubmitButton,
  FormSwitch,
  FormTextField,
} from "../../form-components";
import { useChangeDetection } from "../../../contexts/ChangeDetectionContext";

interface Props {
  workOrderState?: WorkOrderState;
  onSubmitted?: () => void;
  maxOrdinal: number;
  nextOrdinal: number;
}

export const WorkOrderStateForm = (props: Props) => {
  const { workOrderState, onSubmitted, maxOrdinal, nextOrdinal } = props;
  const { setChangeDetection } = useChangeDetection();
  const { mutate: createWorkOrderState } = useAddWorkOrderState();
  const { mutate: patchWorkOrderState } = usePatchWorkOrderState();
  const isCreateMode = !workOrderState;
  const resolver = useMemo(
    () => yupResolver(schema(maxOrdinal)),
    [maxOrdinal]
  );
  const [initialValues, setInitialValues] = useState<
    Partial<WorkOrderState> | undefined
  >(workOrderState ?? { ordinal: nextOrdinal });
  const {
    control,
    watch,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<WorkOrderState>({
    resolver,
    defaultValues: workOrderState ?? { ordinal: nextOrdinal },
  });

  useEffect(() => {
    const subscription = watch(
      (value, { type }) =>
        type === "change" &&
        setChangeDetection(!isEqual(value, initialValues))
    );
    return () => subscription.unsubscribe();
  }, [watch, initialValues, setChangeDetection]);

  useEffect(() => {
    const baseValues = workOrderState ?? { ordinal: nextOrdinal };
    reset(baseValues);
    setInitialValues(baseValues);
    setChangeDetection(false);
  }, [workOrderState, nextOrdinal, reset, setChangeDetection]);

  return (
    <FormContainer
      onSubmit={handleSubmit((workOrderState) => {
        workOrderState.id
          ? patchWorkOrderState(workOrderState)
          : createWorkOrderState(workOrderState);
        onSubmitted && onSubmitted();
        setChangeDetection(false);
      })}
    >
      <Stack direction="row" spacing={2}>
        <FormTextField name="name" label="Name" control={control} />
        <FormTextField
          name="ordinal"
          label="Order"
          control={control}
          type="number"
          inputProps={{ min: 1, max: maxOrdinal + 1 }}
          disabled={isCreateMode}
        />
      </Stack>
      <Stack>
        <FormSwitch
          name="showInDispatch"
          label="Show In Dispatch"
          control={control}
        />
        <FormSwitch
          name="showForTech"
          label="Show For Tech"
          control={control}
        />
      </Stack>
      <FormSubmitButton disabled={isSubmitting} />
    </FormContainer>
  );
};
